/**
 * Alpha metric events (`docs/alpha-scorecard.md`, "Event catalog"). Every
 * event shares one envelope and is written as one NDJSON line (never to
 * Postgres); the server is the only writer, recording its own events and
 * the batches the client posts to `/api/telemetry/client-events`.
 */
import type { EssencePool } from './essence';
import type { CrucibleOutcome } from './network';
import type { VeilSaturationId } from './veil-saturation';

/** Every metric event name, by scorecard family. */
export const METRIC_EVENT_NAMES = [
    // Funnel
    'invite_accepted',
    'discord_authorized',
    'character_created',
    'trial_started',
    'trial_result_inspected',
    'second_session_started',
    // Reliability
    'client_crash',
    'server_error',
    'socket_disconnected',
    'host_launch_failed',
    'lobby_stuck',
    'host_orphaned',
    'map_load_failed',
    /** Once a minute during a run: real-world frame timing (#213). */
    'client_perf_sampled',
    // Gameplay
    'trial_victory',
    'trial_defeat',
    'party_wipe',
    'player_died',
    'map_completed',
    /** A breakable prop broke (#302): one event per party member, with what that member got. */
    'breakable_prop_broken',
    // Integrity
    'reward_transaction_applied',
    'reward_duplicate_rejected',
    'reward_missing_reported',
    'character_state_mismatch',
    'recovery_completed',
    // Community
    'bug_report_received',
    'support_request_received',
    'support_first_response',
    'feedback_received',
] as const;

export type MetricEventName = typeof METRIC_EVENT_NAMES[number];

/** The events the client may post. Anything else in a client batch is refused. */
export const CLIENT_METRIC_EVENT_NAMES = ['client_crash', 'trial_result_inspected', 'client_perf_sampled'] as const satisfies readonly MetricEventName[];

export type ClientMetricEventName = typeof CLIENT_METRIC_EVENT_NAMES[number];

/** The run and content an event belongs to, when it belongs to one. */
export interface MetricEventContext {
    readonly crucibleId?: string;
    readonly mapId?: string;
    readonly partySize?: number;
    readonly contentVersion?: string;
    readonly clientVersion?: string;
}

/**
 * An event as a producer records it. The recorder adds the id, environment
 * and release, and turns `accountId` into a keyed hash before storing it.
 */
export interface MetricEvent extends MetricEventContext {
    readonly eventName: MetricEventName;
    /** ISO time the event happened. */
    readonly occurredAt: string;
    /** The account the event belongs to; never stored as is. */
    readonly accountId?: string;
    readonly payload: Readonly<Record<string, unknown>>;
}

/** One recorded event: a line of the metric event log. Flat, so a log shipper can index it as is. */
export interface MetricEventRecord {
    readonly id: string;
    readonly eventName: MetricEventName;
    readonly occurredAt: string;
    readonly receivedAt: string;
    /** The deploy lane (`dev`, `alpha`, `production`) or `local`. */
    readonly environment: string;
    /** The deploy tag (`<sha8>-<run>-<attempt>`) or `local`. */
    readonly releaseId: string;
    readonly contentVersion: string | null;
    readonly accountIdHash: string | null;
    readonly crucibleId: string | null;
    readonly mapId: string | null;
    readonly partySize: number | null;
    readonly clientVersion: string | null;
    readonly payload: Readonly<Record<string, unknown>>;
}

/** What failed in the browser. */
export type ClientCrashKind =
    /** An uncaught error on the page. */
    | 'error'
    /** A rejected promise nobody handled, on the page. */
    | 'unhandled-rejection'
    /** A React render error caught by an `ErrorBoundary`. */
    | 'render'
    /** An uncaught error or rejection in the game worker. */
    | 'game-worker'
    /** The crucible scene failed before a run took it over. */
    | 'scene'
    /** The game gave up and showed "Something Went Wrong" (`fatalError`). */
    | 'fatal';

/** Every `ClientCrashKind`; a new kind fails to compile until it is listed. */
const CLIENT_CRASH_KIND_KEYS: Readonly<Record<ClientCrashKind, true>> = {
    'error': true,
    'unhandled-rejection': true,
    'render': true,
    'game-worker': true,
    'scene': true,
    'fatal': true,
};

/** Every kind of client crash, for parsing what a client reports. */
export const CLIENT_CRASH_KINDS = Object.keys(CLIENT_CRASH_KIND_KEYS) as readonly ClientCrashKind[];

/** Frame timing at the moment of a crash, from the engine's telemetry. */
export interface ClientCrashFrameTiming {
    readonly fps: number;
    readonly averageFrameTimeMs: number;
    readonly averageRenderTimeMs: number;
    readonly maxFrameTimeMs: number;
    readonly catchUpFrameCount: number;
}

/** `client_crash` payload. */
/**
 * `breakable_prop_broken`'s payload, one per party member (the event's
 * account): every member sees the prop break, and each rolls their own
 * reward.
 */
export interface BreakablePropBrokenPayload {
    /** The prop's map object id. */
    readonly propId: string;
    /** Which prop it was (props.json's id: crate, barrel, vase...). */
    readonly propAssetId: string | null;
    /** Whether this member broke it. */
    readonly isBreaker: boolean;
    /** The essence this member got, by type; null when their roll gave nothing. */
    readonly essence: Partial<EssencePool> | null;
    /** The room depth and Veil Saturation the reward scaled with. */
    readonly depth: number;
    readonly veilSaturation: VeilSaturationId;
}

export interface ClientCrashPayload {
    readonly kind: ClientCrashKind;
    readonly message: string;
    readonly stack?: string;
    /** `location.pathname` when it happened. */
    readonly route: string;
    /** The most recent console errors and warnings, oldest first. */
    readonly consoleTail: readonly string[];
    readonly frameTiming?: ClientCrashFrameTiming;
}

/** `trial_result_inspected` payload: the run's result was shown to the player. */
export interface TrialResultInspectedPayload {
    readonly outcome: CrucibleOutcome;
    readonly reason: string;
    readonly elapsedMs: number;
}

/**
 * `client_perf_sampled` payload: frame timing over about a minute of a run.
 * The percentiles are over the engine's one-second window averages.
 */
export interface ClientPerfSamplePayload {
    readonly sampleWindowMs: number;
    readonly fpsAverage: number;
    readonly frameTimeP50Ms: number;
    readonly frameTimeP95Ms: number;
    readonly frameTimeMaxMs: number;
    readonly renderTimeAverageMs: number;
    readonly catchUpFrameCount: number;
    readonly viewportWidth: number;
    readonly viewportHeight: number;
    readonly devicePixelRatio: number;
    /** The GPU as the browser names it, when it says. */
    readonly gpuRenderer?: string;
}

interface ClientMetricEventEnvelope {
    readonly occurredAt: string;
    readonly crucibleId?: string;
    readonly clientVersion?: string;
}

/** One event in a client batch. The server fills in the rest of the envelope. */
export type ClientMetricEventInput =
    | (ClientMetricEventEnvelope & { readonly eventName: 'client_crash'; readonly payload: ClientCrashPayload })
    | (ClientMetricEventEnvelope & { readonly eventName: 'trial_result_inspected'; readonly payload: TrialResultInspectedPayload })
    | (ClientMetricEventEnvelope & { readonly eventName: 'client_perf_sampled'; readonly payload: ClientPerfSamplePayload });

/** Body of `POST /api/telemetry/client-events`. */
export interface ClientMetricEventBatch {
    readonly events: readonly ClientMetricEventInput[];
}

/** Size limits for client events, shared so the client trims before the server refuses. */
export const CLIENT_METRIC_EVENT_LIMITS = {
    maxEventsPerBatch: 20,
    maxMessageLength: 2000,
    maxStackLength: 8000,
    maxRouteLength: 300,
    maxConsoleLines: 20,
    maxConsoleLineLength: 500,
    maxIdLength: 100,
    maxShortTextLength: 200,
} as const;

/** What a tester says a bug is about. */
export const BUG_REPORT_CATEGORIES = ['crash', 'gameplay', 'visual', 'other'] as const;

export type BugReportCategory = typeof BUG_REPORT_CATEGORIES[number];

/** How each category reads to a tester, in the form and in the Discord post. */
export const BUG_REPORT_CATEGORY_LABELS: Record<BugReportCategory, string> = {
    crash: 'Crash or freeze',
    gameplay: 'Gameplay',
    visual: 'Visual',
    other: 'Something else',
};

/** What the page attaches to a bug report, shown to the tester before sending. */
export interface BugReportContext {
    readonly route: string;
    readonly crucibleId?: string;
    readonly consoleTail: readonly string[];
    readonly frameTiming?: ClientCrashFrameTiming;
    readonly viewportWidth: number;
    readonly viewportHeight: number;
    readonly devicePixelRatio: number;
    readonly gpuRenderer?: string;
    readonly clientVersion: string;
}

/** Body of `POST /api/telemetry/bug-reports` (#218). */
export interface BugReportSubmission {
    readonly category: BugReportCategory;
    readonly description: string;
    readonly expected?: string;
    readonly context: BugReportContext;
    /** A capture of the whole tab (#235), when the tester chose to attach one. */
    readonly screenshot?: BugReportScreenshot;
}

/** A JPEG of the tab, base64 encoded so it travels in the JSON body. */
export interface BugReportScreenshot {
    readonly mimeType: 'image/jpeg';
    readonly dataBase64: string;
}

/** Answer to a bug report: the id the tester can quote. */
export interface BugReportReceipt {
    /** The report's GUID; testers quote its first eight characters. */
    readonly reportId: string;
}

export const BUG_REPORT_LIMITS = {
    maxDescriptionLength: 2000,
    maxExpectedLength: 2000,
    /** Longest side of a screenshot; the client scales larger captures down. */
    maxScreenshotDimension: 1920,
    /** Decoded size of a screenshot. A 1920-wide JPEG at the client's quality is well under this. */
    maxScreenshotBytes: 1_500_000,
} as const;
